/**
 * Discipline-specific marketing content for /disciplines/:slug pages.
 * Each entry tells a clinic owner: this is what your week looks like,
 * here's what hurts, here's what changes, here's the stack we'd recommend.
 */
import {
  Sparkles, Activity, Stethoscope, Apple, Flower, HandMetal,
  PhoneCall, Mail, MessageSquare, Calendar, Repeat, Star,
  CreditCard, ListChecks, Send, Heart, BookOpen, Instagram, BadgePercent,
  Receipt, Bell,
} from "lucide-react";

export const DISCIPLINES = [
  {
    slug: "physiotherapy",
    name: "Physiotherapy",
    icon: Activity,
    color: "from-blue-500 to-cyan-500",
    accent: "text-blue-500",
    bg: "bg-blue-500/10",
    ring: "ring-blue-500/30",
    fromHex: "#0ea5e9",
    toHex:   "#06b6d4",
    tagline: "Recover patients faster — and stop losing them between sessions.",
    hero: "Built for the realities of running a physio clinic — packed schedules, insurance follow-ups, treatment plans that span 8–12 visits, and patients who quietly stop coming.",
    pains: [
      { title: "No-shows kill your day",            body: "One missed slot = $120 gone and a domino effect on the rest of your column." },
      { title: "Treatment plan drop-off",           body: "Patients ghost after visit 3. By visit 6 you've lost half. You don't know why." },
      { title: "Insurance follow-ups eat hours",    body: "Reception spends mornings re-billing instead of booking." },
    ],
    outcomes: [
      { stat: "+41%",      label: "treatment plan completion",  foot: "from ~38% to ~71% on average" },
      { stat: "22 hrs",    label: "saved per clinic per week",  foot: "reception + insurance work" },
      { stat: "$8,400",    label: "monthly no-show recovery",   foot: "auto-rebook flow" },
      { stat: "< 12 days", label: "to fully go live",           foot: "EMR sync included" },
    ],
    comparison: [
      { metric: "Plan completion rate",   before: "38%",        after: "71%" },
      { metric: "Reception calls per day", before: "35 outgoing", after: "12 outgoing · KAI handles the rest" },
      { metric: "Stale patient list",     before: "We have one?", after: "Auto-worked daily" },
      { metric: "Saturday admin",         before: "2–3 hrs",    after: "0 hrs" },
    ],
    workflows: [
      { Icon: Repeat,       title: "Plan-drop reactivation",   body: "Patients who miss visit 3 get a warm SMS within 24h." },
      { Icon: Calendar,     title: "Same-day rebook",          body: "KAI rebooks before the patient leaves the room." },
      { Icon: Receipt,      title: "Insurance follow-up",      body: "Claim status surfaced in the inbox — no calls to insurer." },
      { Icon: ListChecks,   title: "Treatment plan reminders", body: "Each plan visit auto-pings the patient 24h before." },
      { Icon: Star,         title: "Outcome review request",   body: "Sent only after the goal-met visit — 3× more 5-stars." },
    ],
    faqs: [
      { q: "Will Clinic OS work with my EMR?",                a: "Native to Juvonno and Jane App. We sync calendar, charts and notes both directions in real time." },
      { q: "How long to fully onboard?",                      a: "Most physio clinics are live in under 12 days — including EMR sync, AI Receptionist tuning and patient backfill." },
      { q: "Can it handle insurance claim follow-ups?",        a: "Yes. Insurance status syncs into the unified inbox so reception sees who owes what without re-checking the EMR." },
      { q: "What if my reception team is small or solo?",      a: "Even better — the AI Receptionist becomes your night and lunch-break coverage from day 1." },
      { q: "How much does it cost?",                          a: "Configure your stack on the Build Your OS page — most physio clinics land in the Hub+ tier." },
    ],
    recommendedBase: "hub-plus",
    recommendedAddons: ["ai-workforce", "sales-autopilot", "clinic-pulse"],
    why: [
      "AI Workforce answers patient calls and books the next visit before they hang up.",
      "Sales Autopilot pings patients who skipped visit 3 — gets them back in the chair.",
      "Clinic Pulse shows you exactly which therapist has the best retention and why.",
    ],
    typicalDay: [
      { time: "Mon 8:02 AM",  event: "AI Receptionist books 3 new patients before reception even arrives." },
      { time: "Mon 11:15 AM", event: "Sales Autopilot re-engages 7 patients who stalled mid-treatment plan." },
      { time: "Fri 5:30 PM",  event: "Clinic Pulse Weekly: revenue up 18%, no-shows down 22%, retention up across the board." },
    ],
    quote: {
      text: "We added Clinic OS in Q2 and rebooked 41 stale patients in the first month. The AI Receptionist alone paid for the platform.",
      author: "Clinic Owner · Physiotherapy practice, Ontario",
    },
  },

  {
    slug: "chiropractic",
    name: "Chiropractic",
    icon: Sparkles,
    color: "from-violet-500 to-fuchsia-500",
    accent: "text-violet-500",
    bg: "bg-violet-500/10",
    ring: "ring-violet-500/30",
    fromHex: "#7c3aed",
    toHex:   "#d946ef",
    tagline: "Keep adjustment patients on plan — and bring back the ones who fell off.",
    hero: "Chiropractic care thrives on consistency. Clinic OS makes sure patients show up for every adjustment, your front desk never misses a call, and your reactivation list works for you.",
    pains: [
      { title: "Patients skip the maintenance phase", body: "They come for pain relief, then disappear before wellness plans kick in." },
      { title: "Phones ring during adjustments",      body: "Either you stop a session or you miss the lead. Both cost money." },
      { title: "Reviews are spotty",                   body: "Patients love you — they just forget to leave a Google review." },
    ],
    outcomes: [
      { stat: "71%",       label: "maintenance phase retention", foot: "up from 38%" },
      { stat: "3.2×",      label: "more Google 5-star reviews",  foot: "auto-requested post visit 3 & 6" },
      { stat: "0",         label: "missed calls during adjustments", foot: "AI Receptionist takes them" },
      { stat: "< 14 days", label: "to fully go live",            foot: "incl. review automation" },
    ],
    comparison: [
      { metric: "Wellness plan conversion",        before: "38%",  after: "71%" },
      { metric: "Missed calls during adjustments", before: "4–5 / day", after: "0" },
      { metric: "Google reviews per month",        before: "8",    after: "26" },
      { metric: "Maintenance churn",               before: "50%",  after: "22%" },
    ],
    workflows: [
      { Icon: PhoneCall,   title: "AI receives every call",   body: "Stays on as you adjust. Booking happens without you leaving the room." },
      { Icon: Star,        title: "Review automation",        body: "Request fires 2h after visit 3 — the happiest moment." },
      { Icon: Heart,       title: "Wellness plan upsell",     body: "Sent at the right visit, with the right wording — 71% take it." },
      { Icon: Repeat,      title: "Maintenance reactivation", body: "30-day silent patients auto-pinged with care, not pressure." },
      { Icon: Calendar,    title: "Adjustment reminders",     body: "Day-before SMS confirms or reschedules instantly." },
    ],
    faqs: [
      { q: "Does it work alongside my EMR?",           a: "Yes — native EMR sync (Juvonno today, Jane and others rolling out)." },
      { q: "What about responding to 1-star reviews?",  a: "KAI drafts a calm, accountable reply — your manager approves in 1 click before it goes live." },
      { q: "Multi-location chiropractic group?",       a: "Yes — Clinic Pulse rolls up KPIs across locations and shows which DC has the strongest retention." },
      { q: "How long does onboarding take?",            a: "Most chiropractic clinics are live in under 14 days." },
      { q: "What if I want to cancel?",                 a: "Disconnect in one click. Your EMR keeps every record." },
    ],
    recommendedBase: "hub-plus",
    recommendedAddons: ["ai-workforce", "growth", "academy"],
    why: [
      "AI Receptionist takes every call so you stay in the room with the patient.",
      "Growth automates Google review requests after appointment 3 and 6.",
      "Academy lets you sell wellness programs (posture, ergonomics) as paid courses.",
    ],
    typicalDay: [
      { time: "Daily",   event: "AI Workforce handles every front-desk call — zero missed leads while you adjust." },
      { time: "Weekly",  event: "Review AI auto-responds to every Google review, 5★ and otherwise." },
      { time: "Monthly", event: "Academy course 'Posture Reset' sells 22 seats — pure margin." },
    ],
    quote: {
      text: "Google reviews tripled in 90 days. Our wellness plan retention went from 38% to 71%.",
      author: "DC · Multi-location chiropractic group",
    },
  },

  {
    slug: "dental",
    name: "Dental",
    icon: Stethoscope,
    color: "from-sky-500 to-cyan-500",
    accent: "text-sky-500",
    bg: "bg-sky-500/10",
    ring: "ring-sky-500/30",
    fromHex: "#0284c7",
    toHex:   "#06b6d4",
    tagline: "Fill your hygiene schedule. Recall the patients you haven't seen in 9 months.",
    hero: "Dental clinics live and die by recall. Clinic OS automates the calls, the SMS, the email — and the AI can even read the call recording to tell you exactly why a patient didn't book.",
    pains: [
      { title: "Recall is a manual nightmare",        body: "Reception cold-calls inactive patients between cleanings. Hit rate is low and morale is lower." },
      { title: "Hygiene gaps cost thousands/week",    body: "Every empty hygiene chair is $150 missing." },
      { title: "Insurance claim status is a black hole", body: "Patients call asking and reception has no answer fast." },
    ],
    outcomes: [
      { stat: "92%",       label: "hygiene chair utilisation",    foot: "up from ~76%" },
      { stat: "168",       label: "inactive patients recovered",  foot: "in the first 6 months" },
      { stat: "$94k",      label: "production recovered",         foot: "from the inactive list alone" },
      { stat: "< 12 days", label: "to fully go live",             foot: "Open Dental + Dentrix support" },
    ],
    comparison: [
      { metric: "Hygiene utilisation",       before: "76%",            after: "92%" },
      { metric: "Inactive patient outreach", before: "30 manual / wk", after: "200 auto / wk" },
      { metric: "Recall → booking rate",     before: "18%",            after: "41%" },
      { metric: "Insurance answer time",     before: "5 min / call",   after: "Instant in inbox" },
    ],
    workflows: [
      { Icon: Bell,        title: "9-month recall waterfall",   body: "SMS · voice · email at the right intervals — accept rate 41%." },
      { Icon: PhoneCall,   title: "Hygiene chair fill",         body: "Reactivation list works the empty chairs automatically." },
      { Icon: Receipt,     title: "Insurance status sync",      body: "Status visible in inbox the moment patient calls in." },
      { Icon: BadgePercent,title: "Treatment plan acceptance",  body: "Stale treatment plans get re-pitched with the right wording." },
      { Icon: Repeat,      title: "Missed-appointment recovery", body: "Auto-rebooked within 48h via SMS + voice." },
    ],
    faqs: [
      { q: "Open Dental and Dentrix support?",     a: "Yes — both supported. We sync schedule, hygiene chairs and patient records in real time." },
      { q: "Insurance integration?",                 a: "Claim status reads back into the unified inbox so reception always has an answer ready." },
      { q: "Multi-chair / multi-location practice?", a: "Yes — Clinic Pulse rolls up production, hygiene utilisation and no-show rate per chair, per location." },
      { q: "HIPAA?",                                  a: "TLS 1.3, AES-256 at rest, per-clinic isolated tenants, full audit log per record write." },
      { q: "Onboarding timeline?",                    a: "Most dental practices are live in under 12 days, including EMR sync and recall calibration." },
    ],
    recommendedBase: "hub-plus",
    recommendedAddons: ["ai-workforce", "call-iq-ai", "sales-autopilot"],
    why: [
      "AI Workforce books recall appointments automatically — SMS, voice and email simultaneously.",
      "Call IQ AI tells you exactly which calls converted and which didn't — and why.",
      "Sales Autopilot resurfaces patients overdue for 9+ months and books them.",
    ],
    typicalDay: [
      { time: "Daily 9 AM", event: "Sales Autopilot loads 15 overdue patients into the day's reactivation queue." },
      { time: "Daily PM",   event: "Call IQ AI summarizes every call: who booked, who hesitated, who needs a follow-up." },
      { time: "Weekly",     event: "Hygiene utilization above 92% — for the first time in 3 years." },
    ],
    quote: {
      text: "We recovered 168 inactive patients in the first 6 months. That's $94k in production we would have left on the table.",
      author: "Office Manager · 3-chair dental practice",
    },
  },

  {
    slug: "dietetics",
    name: "Dietetics & Nutrition",
    icon: Apple,
    color: "from-emerald-500 to-teal-500",
    accent: "text-emerald-500",
    bg: "bg-emerald-500/10",
    ring: "ring-emerald-500/30",
    fromHex: "#10b981",
    toHex:   "#14b8a6",
    tagline: "Sell programs, not single sessions. Keep clients engaged between visits.",
    hero: "Nutrition is sold on outcomes that take weeks. Clinic OS keeps clients accountable between appointments and gives you a recurring-revenue model with Academy.",
    pains: [
      { title: "One-and-done consults",     body: "Clients come once, ghost, then go buy a generic plan online." },
      { title: "Hard to scale 1:1",         body: "You only have so many hours. Higher revenue means group programs you can't quite operationalize." },
      { title: "Engagement drops between visits", body: "Clients lose momentum on day 4. By day 10 they're back to old habits." },
    ],
    outcomes: [
      { stat: "$4k/mo",    label: "recurring revenue per RD",   foot: "via Academy programs" },
      { stat: "71%",       label: "client adherence at week 4", foot: "up from ~38%" },
      { stat: "31%",       label: "discovery → program rate",   foot: "from automated funnel" },
      { stat: "< 10 days", label: "to launch your first cohort", foot: "Academy + Hub+ combined" },
    ],
    comparison: [
      { metric: "Revenue model",          before: "1:1 hourly",    after: "1:1 + group programs" },
      { metric: "Coaching hours per week", before: "30 / wk",      after: "15 / wk + group" },
      { metric: "Lead → discovery call",   before: "12%",          after: "38%" },
      { metric: "Client adherence (wk 4)", before: "~38%",         after: "~71%" },
    ],
    workflows: [
      { Icon: Send,        title: "Lead → discovery call",   body: "Funnel · AI qualifier · booking — all in one flow." },
      { Icon: MessageSquare, title: "Day-4 check-in",         body: "Warm SMS at the moment momentum drops." },
      { Icon: BookOpen,    title: "Program upsell",          body: "Pitched after first 1:1 wraps — 31% conversion." },
      { Icon: Heart,       title: "Habit tracker nudges",    body: "Weekly accountability ping that doesn't feel automated." },
      { Icon: Mail,        title: "Meal-plan Q&A",           body: "AI answers nutrition questions 24/7 between visits." },
    ],
    faqs: [
      { q: "Can I really sell programs through this?",   a: "Yes — Academy is built for it. Drag-and-drop module builder, payment + drip schedule baked in." },
      { q: "Group coaching support?",                     a: "Yes — community + group cohorts inside Academy. Live calls, async chat, certification paths." },
      { q: "Refund handling?",                             a: "Stripe-native refunds + cancellation flows. Reasons captured so you learn what's working." },
      { q: "Solo RD vs multi-practitioner clinic?",        a: "Both supported. Hub fits solo RDs; Hub+ unlocks multi-practitioner workspaces." },
      { q: "Onboarding timeline?",                          a: "Most nutrition practices launch their first cohort within 10 days." },
    ],
    recommendedBase: "hub-plus",
    recommendedAddons: ["academy", "ai-workforce", "growth"],
    why: [
      "Academy turns your method into a sellable program — passive recurring revenue.",
      "AI Workforce handles meal-plan questions 24/7 so clients stay engaged.",
      "Growth funnels new leads into your discovery call.",
    ],
    typicalDay: [
      { time: "Onboarding", event: "Lead lands in Growth funnel → AI Workforce qualifies → discovery call booked." },
      { time: "Engagement", event: "Copilot DMs clients on day 4 with a 'how's it going?' that actually feels human." },
      { time: "Retention",  event: "Academy module 'Sustainable Habit Loops' sells $297/seat to clients after the 1:1 wraps." },
    ],
    quote: {
      text: "I went from 1:1 hourly billing to a $4k/mo program model in 5 months. Clinic OS handles the operations so I can coach.",
      author: "RD · Functional nutrition practice",
    },
  },

  {
    slug: "spa",
    name: "Medical Spa",
    icon: Flower,
    color: "from-rose-500 to-pink-500",
    accent: "text-rose-500",
    bg: "bg-rose-500/10",
    ring: "ring-rose-500/30",
    fromHex: "#f43f5e",
    toHex:   "#ec4899",
    tagline: "Turn first-time guests into membership patients.",
    hero: "Med spa margins live in repeat visits. Clinic OS makes sure every guest goes from first treatment to membership — with AI follow-ups that don't feel salesy.",
    pains: [
      { title: "First-timers don't rebook",   body: "Loved the treatment, forgot to book the next. You've lost them to the next ad they see." },
      { title: "Reviews are gold but spotty", body: "Med spa decisions are review-driven. Yours are too few and too random." },
      { title: "DMs from Instagram pile up",  body: "Reception can't keep up. Leads cool while they wait." },
    ],
    outcomes: [
      { stat: "2×",        label: "membership conversion",      foot: "1st visit → membership" },
      { stat: "60 sec",    label: "Instagram DM reply time",    foot: "down from hours" },
      { stat: "31%",       label: "3-pack accept rate",         foot: "offered at the right moment" },
      { stat: "< 10 days", label: "to fully go live",           foot: "incl. IG · Stripe · review automation" },
    ],
    comparison: [
      { metric: "Instagram DM reply time",   before: "Hours",  after: "60 seconds" },
      { metric: "First-visit rebook rate",   before: "28%",    after: "64%" },
      { metric: "Reviews per month",         before: "6",      after: "22" },
      { metric: "Membership conversion",     before: "18%",    after: "36%" },
    ],
    workflows: [
      { Icon: Instagram,   title: "IG DM → booking funnel", body: "Reply in 60s · qualify · book — fully automated." },
      { Icon: BadgePercent,title: "3-pack offer timing",    body: "Sent 48h post-treatment when results peak." },
      { Icon: Heart,       title: "Membership conversion",  body: "Personalised pitch after visit 2 — 36% take it." },
      { Icon: Star,        title: "Review on autopilot",    body: "Request fires 2h post-treatment · before the next ad steals attention." },
      { Icon: PhoneCall,   title: "Treatment upsell",       body: "AI Receptionist suggests complementary services on inbound calls." },
    ],
    faqs: [
      { q: "Instagram + Facebook DM integration?", a: "Native. Direct OAuth with Meta — DMs land in the same inbox as SMS and calls." },
      { q: "Multi-location med spa pricing?",       a: "Hub+ at $597/mo per location, then add the modules you actually want. Configure on Build Your OS." },
      { q: "HIPAA + cosmetic compliance?",          a: "Full HIPAA stack. Photos, before/afters and consent forms are all encrypted and audit-logged." },
      { q: "Stripe payments + memberships?",         a: "Yes — recurring billing, package credits, and one-click upgrades baked in." },
      { q: "Onboarding timeline?",                    a: "Most med spas are live in under 10 days, including IG and Stripe." },
    ],
    recommendedBase: "hub-plus",
    recommendedAddons: ["growth", "ai-workforce", "sales-autopilot"],
    why: [
      "Growth runs the Instagram → DM → booking funnel automatically.",
      "AI Workforce replies to every DM instantly — no lead dies cold.",
      "Sales Autopilot turns first-time guests into 3-pack and membership patients.",
    ],
    typicalDay: [
      { time: "Always-on",      event: "Every Instagram DM replied to within 60 seconds — by AI Workforce." },
      { time: "Post-treatment", event: "Sales Autopilot offers a 3-pack at the right moment — accept rate ~31%." },
      { time: "Reviews",        event: "Review AI responds to every 5★ and 4★ review with a personal touch." },
    ],
    quote: {
      text: "Membership conversions doubled. The AI replies to my IG faster than I do — and books treatments without me lifting a finger.",
      author: "Owner · Med Spa, Toronto",
    },
  },

  {
    slug: "massage",
    name: "Massage Therapy",
    icon: HandMetal,
    color: "from-amber-500 to-orange-500",
    accent: "text-amber-500",
    bg: "bg-amber-500/10",
    ring: "ring-amber-500/30",
    fromHex: "#f59e0b",
    toHex:   "#f97316",
    tagline: "Stay booked solid without doing reception work between sessions.",
    hero: "RMTs deliver care in 60-minute blocks — there's no time for reception work. Clinic OS handles bookings, reminders and rebooks so you stay in the room.",
    pains: [
      { title: "Phone tag eats lunch",            body: "You're returning calls between clients instead of resting or eating." },
      { title: "Rebooks are inconsistent",        body: "Half your clients book their next session, the other half need to be chased." },
      { title: "Insurance receipts pile up",      body: "End-of-week admin steals your Friday evening." },
    ],
    outcomes: [
      { stat: "82%",     label: "rebook rate",                 foot: "up from ~55%" },
      { stat: "0",       label: "missed calls per day",        foot: "AI Receptionist catches them all" },
      { stat: "5 hrs",   label: "reception time saved weekly", foot: "phone tag · admin · receipts" },
      { stat: "< 7 days",label: "to fully go live",            foot: "Jane App + Hub" },
    ],
    comparison: [
      { metric: "Missed calls per day",  before: "4–5",     after: "0" },
      { metric: "Rebook rate",            before: "55%",    after: "82%" },
      { metric: "Friday admin",           before: "2 hrs",  after: "0 hrs" },
      { metric: "Phone tag per day",      before: "~25 min",after: "0 min" },
    ],
    workflows: [
      { Icon: PhoneCall, title: "AI receives every call",   body: "Books and reschedules without you leaving the table." },
      { Icon: Repeat,    title: "Auto-rebook prompt",       body: "Day-after SMS asks 'same time next week?' — 82% accept." },
      { Icon: Receipt,   title: "Insurance receipt batch",  body: "Friday auto-generates the week's receipts. Inbox-ready." },
      { Icon: Bell,      title: "Day-before reminders",     body: "Confirm or reschedule with one tap — no-shows drop." },
      { Icon: CreditCard,title: "Mid-session payment",      body: "Patient pays via SMS link, you stay in the room." },
    ],
    faqs: [
      { q: "Solo RMT pricing?",         a: "Hub at $297/mo fits most solo practitioners. Add modules only when you need them." },
      { q: "Insurance receipts?",        a: "Yes — auto-generated, batched weekly, and emailed in one click." },
      { q: "Jane App support?",           a: "Native. Schedule, chart and billing stay in Jane — Clinic OS adds the front desk and outreach layer." },
      { q: "Mobile app for therapists?",  a: "Yes — manage your day, calls, reminders and payments from your phone between sessions." },
      { q: "Cancellation policy?",        a: "Disconnect in one click. Your Jane data stays in Jane." },
    ],
    recommendedBase: "hub",
    recommendedAddons: ["ai-workforce", "academy"],
    why: [
      "AI Workforce handles every booking, reminder and rebook — even mid-massage.",
      "Hub keeps your inbox, scheduling and EMR in one place — no juggling.",
      "Academy lets you sell self-care courses to clients between sessions.",
    ],
    typicalDay: [
      { time: "Always",      event: "AI Receptionist books and reschedules without you leaving the room." },
      { time: "Weekly",      event: "Rebook rate climbs from ~55% to ~82%." },
      { time: "Friday 5 PM", event: "Hub auto-generates insurance receipts for the week. Lunch break, back." },
    ],
    quote: {
      text: "I solo-run my practice and was missing 4–5 calls a day. Now I miss zero — and I'm not glued to my phone.",
      author: "RMT · Solo practitioner, Calgary",
    },
  },
];

export const disciplineBySlug = (slug) => DISCIPLINES.find((d) => d.slug === slug);
